/**
 * Enhanced device detection utility
 */

export const getDeviceInfo = () => {
  // Check for simulation mode via URL parameter or localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const simulateDevice = urlParams.get('simulate') || localStorage.getItem('simulate-device');

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const platform = navigator.platform || '';

  // Detect iOS
  let isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  let isIPad = /iPad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Detect Android
  let isAndroid = /android/i.test(userAgent);

  // Apply simulation overrides
  if (simulateDevice === 'ios') {
    isIOS = true;
    isIPad = false;
    isAndroid = false;
  } else if (simulateDevice === 'ipad') {
    isIOS = true;
    isIPad = true;
    isAndroid = false;
  } else if (simulateDevice === 'android') {
    isIOS = false;
    isIPad = false;
    isAndroid = true;
  }

  // Detect specific Android devices
  const isAndroidPhone = isAndroid && /mobile/i.test(userAgent);
  const isAndroidTablet = isAndroid && !/mobile/i.test(userAgent);

  // Detect desktop operating systems
  const isWindows = /Win/.test(platform);
  const isMacOS = /Mac/.test(platform) && !isIPad;
  const isLinux = /Linux/.test(platform) && !isAndroid;

  // Detect mobile vs desktop
  const isMobile = isIOS || isAndroid;
  const isTablet = isIPad || isAndroidTablet;
  const isDesktop = simulateDevice === 'desktop' || (!isMobile && !simulateDevice);

  // Detect browser
  const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
  const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(userAgent);
  const isEdge = /Edg/.test(userAgent);

  // Detect if PWA is supported
  const supportsPWA = 'serviceWorker' in navigator && 'BeforeInstallPromptEvent' in window;

  // Detect if running as PWA
  const isRunningAsPWA = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone ||
                         document.referrer.includes('android-app://');

  // Determine primary device type
  let deviceType = 'desktop';
  if (isIOS) deviceType = 'ios';
  else if (isAndroid) deviceType = 'android';

  return {
    deviceType,
    isIOS,
    isIPad,
    isAndroid,
    isAndroidPhone,
    isAndroidTablet,
    isWindows,
    isMacOS,
    isLinux,
    isMobile,
    isTablet,
    isDesktop,
    isChrome,
    isSafari,
    isFirefox,
    isEdge,
    supportsPWA,
    isRunningAsPWA,
    userAgent,
    platform,
    isSimulated: !!simulateDevice
  };
};

/**
 * Check if native app is installed using deep link detection
 */
export const checkAppInstalled = (deepLink, timeout = 2000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    let hasFocus = true;

    const onBlur = () => {
      hasFocus = false;
    };

    const onFocus = () => {
      hasFocus = true;
    };

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    // Try to open the deep link
    window.location.href = deepLink;

    // Check if the app opened (user left the page)
    setTimeout(() => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);

      const elapsed = Date.now() - startTime;

      // If we lost focus or significant time passed, app likely opened
      if (!hasFocus || elapsed > timeout + 100) {
        resolve(true);
      } else {
        resolve(false);
      }
    }, timeout);
  });
};

/**
 * Set device simulation mode
 */
export const setDeviceSimulation = (device) => {
  if (device && ['ios', 'ipad', 'android', 'desktop'].includes(device)) {
    localStorage.setItem('simulate-device', device);
  } else {
    localStorage.removeItem('simulate-device');
  }
  window.location.reload();
};

/**
 * Clear device simulation mode
 */
export const clearDeviceSimulation = () => {
  localStorage.removeItem('simulate-device');
  window.location.reload();
};

/**
 * Get current simulation mode
 */
export const getSimulationMode = () => {
  return localStorage.getItem('simulate-device');
};

export default getDeviceInfo;
